import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/redux/store";
import { login as loginAction, logout } from "@/redux/authSlice";
import { refreshUserAuth } from "@/lib/api";

const PROTECTED_SESSION_PATH_PREFIXES = [
  "/dashboard-",
  "/userprofile",
  "/profile-settings",
  "/addproject",
  "/addtransaction",
  "/sendupdate",
  "/admin",
  "/project-reporting",
  "/reports/view",
  "/auth/stripe/oauth/callback",
];

const SessionManager: React.FC = () => {
  const { isLoggedIn, token } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(300); // 5 minutes in seconds

  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  // Decode JWT to get expiration time
  const getTokenExpiry = useCallback((token: string): number | null => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
    } catch (error) {
      console.error("Failed to decode token:", error);
      return null;
    }
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current);

    dispatch(logout());
    setShowWarning(false);

    const currentPath = window.location.pathname;
    const isProtectedPath = PROTECTED_SESSION_PATH_PREFIXES.some((prefix) =>
      currentPath.startsWith(prefix)
    );

    if (isProtectedPath) {
      window.location.href = "/login";
    }
  }, [dispatch]);

  // Extend session by refreshing token
  const handleExtendSession = useCallback(async () => {
    if (isRefreshingRef.current) return;

    isRefreshingRef.current = true;
    setShowWarning(false);

    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current);

    try {
      console.log("🔄 Extending session...");
      const data = await refreshUserAuth(token as string);

      if (data) {
        dispatch(
          loginAction({
            token: data.token,
            firstname: data.firstname,
            lastname: data.lastname,
            email: data.email,
            bio: data.bio,
            introduction: data.introduction,
            charities: data.charities,
            donorOrgs: data.donorOrgs,
            projects: data.projects,
            userProjectMemberships: data.userProjectMemberships,
            userId: data.id,
            avatarid: data.avatarid,
            userType: data.userType,
            walletAddress: data.walletAddress,
            roles: data.roles,
            marketingEmailConsent: data.marketingEmailConsent || false,
            impactUpdateConsent:
              data.impactUpdateConsent !== undefined
                ? data.impactUpdateConsent
                : true,
            preferredCurrency: data.preferredCurrency || null,
          }),
        );
        console.log("✅ Session extended successfully");
      }
    } catch (error) {
      console.error("❌ Failed to extend session:", error);
      handleLogout();
    } finally {
      isRefreshingRef.current = false;
    }
  }, [token, dispatch, handleLogout]);

  // Setup warning and logout timers based on token expiry
  useEffect(() => {
    if (!isLoggedIn || !token) {
      setShowWarning(false);
      return;
    }

    const expiryTime = getTokenExpiry(token);
    if (!expiryTime) {
      console.error("Could not determine token expiry");
      return;
    }

    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;
    const warningTime = timeUntilExpiry - 5 * 60 * 1000; // 5 minutes before expiry

    console.log(
      `Token expires in ${Math.floor(timeUntilExpiry / 1000 / 60)} minutes`,
    );

    // If already expired
    if (timeUntilExpiry <= 0) {
      handleLogout();
      return;
    }

    // If less than 5 minutes until expiry, show warning immediately
    if (warningTime <= 0) {
      setShowWarning(true);
      setCountdown(Math.floor(timeUntilExpiry / 1000));

      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current!);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      logoutTimerRef.current = setTimeout(handleLogout, timeUntilExpiry);
    } else {
      // Set timer to show warning 5 minutes before expiry
      warningTimerRef.current = setTimeout(() => {
        setShowWarning(true);
        setCountdown(300); // 5 minutes

        countdownIntervalRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current!);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        logoutTimerRef.current = setTimeout(handleLogout, 5 * 60 * 1000);
      }, warningTime);
    }

    // Cleanup
    return () => {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (countdownIntervalRef.current)
        clearInterval(countdownIntervalRef.current);
    };
  }, [isLoggedIn, token, getTokenExpiry, handleLogout]);

  // Don't render if not logged in or warning not showing
  if (!isLoggedIn || !showWarning) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="mx-4 z-50 w-full max-w-md rounded-lg text-gray-900 dark:text-gray-300 bg-white dark:bg-gray-800 p-6 shadow-xl">
        <div className="mb-4 flex items-start">
          <div className="flex-shrink-0">
            <svg
              className="h-6 w-6 text-amber-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-300">
              Session Expiring Soon
            </h3>
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              <p>Your session will expire in:</p>
              <div className="mt-3 text-center">
                <span className="text-3xl font-bold text-amber-600">
                  {String(minutes).padStart(2, "0")}:
                  {String(seconds).padStart(2, "0")}
                </span>
              </div>
              <p className="mt-3">
                Click "Extend Session" to stay logged in, or you'll be
                automatically logged out.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={() => setShowWarning(false)}
            className="rounded-md bg-gray-100 px-4 py-2 cursor-pointer text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
          >
            Dismiss
          </button>
          <button
            onClick={handleExtendSession}
            className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Extend Session
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionManager;
